import { Link, useLocation } from "react-router-dom";
import { FiGithub, FiYoutube } from "react-icons/fi";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

const navItems = [
  // Par usage : l'outil le plus consulté d'abord, puis les cours, puis les tutoriels.
  { label: "Home", to: "/" },
  { label: "LLM VRAM Calculator", to: "/llm-vram-calculator" },
  { label: "Gradient Descent", to: "/gradient-descent" },
  { label: "MCP", to: "/mcp" },
  { label: "AI/ML Architect", to: "/ai-ml-architect" },
  { label: "Lakehouse", to: "/lakehouse" },
  { label: "Compiler", to: "/compiler" },
];

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Ferme le menu à chaque changement de route
  useEffect(() => setIsMenuOpen(false), [location.pathname]);

  // Overlay plein écran : on verrouille le scroll et on capte Échap
  useEffect(() => {
    if (!isMenuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setIsMenuOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [isMenuOpen]);

  return (
    <>
      {/* Lien d'évitement : invisible sauf au clavier (premier Tab), il mène au contenu de la page
          sans repasser par les liens de navigation. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-[#070b0a] focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:outline-2 focus:outline-offset-2 focus:outline-cyan-400"
        onClick={(e) => {
          // Chaque page porte <main id="main"> : l'ancre est valide, le clic y met aussi le focus.
          const main = document.getElementById("main");
          if (!main) return;
          e.preventDefault();
          main.setAttribute("tabindex", "-1");
          main.focus();
        }}
      >
        Skip to content
      </a>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "border-b border-white/10 bg-[#070b0a]/80 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <nav
          aria-label="Main"
          className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-6 sm:px-8 lg:px-12"
        >
          <Link to="/" className="flex items-center gap-2.5">
            {/* Affiché sur 32 px : la version 64 px (2,5 Ko) suffit jusqu'aux écrans 2x, 96 px au-delà. */}
            <img
              src="/logo-64.webp"
              srcSet="/logo-64.webp 64w, /logo.webp 96w"
              sizes="32px"
              alt=""
              width={96}
              height={71}
              className="h-8 w-8 rounded-lg object-contain"
              loading="eager"
              decoding="async"
            />
            <span className="font-inter-nav text-[15px] font-semibold tracking-tight text-white">
              StudioTV
            </span>
          </Link>

          <ul className="hidden items-center gap-7 lg:flex">
            {navItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  data-active={location.pathname === item.to}
                  className="nav-link font-inter-nav text-white/75"
                  style={{ fontSize: "16px" }}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-4">
            <a
              href="https://github.com/StudioTV/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub (opens in a new tab)"
              className="nav-icon -m-2 hidden p-2 transition-colors sm:block"
            >
              <FiGithub size={18} aria-hidden="true" />
            </a>
            <a
              href="https://www.youtube.com/@StudioTV"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube (opens in a new tab)"
              className="nav-icon -m-2 hidden p-2 transition-colors sm:block"
            >
              <FiYoutube size={18} aria-hidden="true" />
            </a>

            <button
              type="button"
              onClick={() => setIsMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              className="btn-plain -mr-2 flex h-11 w-11 items-center justify-center lg:hidden"
            >
              <Menu size={22} />
            </button>
          </div>
        </nav>
      </header>

      {/* Overlay plein écran mobile */}
      <div
        id="mobile-menu"
        hidden={!isMenuOpen}
        className="fixed inset-0 z-[60] bg-[#070b0a] lg:hidden"
      >
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-6 sm:px-8">
          <span className="font-inter-nav text-[15px] font-semibold tracking-tight text-white">
            StudioTV
          </span>
          <button
            type="button"
            onClick={() => setIsMenuOpen(false)}
            aria-label="Close menu"
            className="btn-plain -mr-2 flex h-11 w-11 items-center justify-center"
          >
            <X size={22} />
          </button>
        </div>

        <nav aria-label="Mobile" className="px-6 pt-6 sm:px-8">
          <ul className="flex flex-col gap-1">
            {navItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  data-active={location.pathname === item.to}
                  className="nav-link block py-3 font-inter-nav text-2xl font-medium text-white/85"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex items-center gap-6 border-t border-white/10 pt-6">
            <a
              href="https://github.com/StudioTV/"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-icon -my-3 flex items-center gap-2 py-3 text-sm transition-colors"
            >
              <FiGithub size={18} aria-hidden="true" /> GitHub
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
            <a
              href="https://www.youtube.com/@StudioTV"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-icon -my-3 flex items-center gap-2 py-3 text-sm transition-colors"
            >
              <FiYoutube size={18} aria-hidden="true" /> YouTube
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </div>
        </nav>
      </div>
    </>
  );
}
